import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Building2, Mail, Briefcase, FileText, ExternalLink, Target, Save, Receipt, Link2, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useClientDrawer } from "./ClientDrawerProvider";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

/**
 * UniversalClientDrawer — REX v3 Phase 3
 *
 * Slide-over from any client name across the app. Fetches client identity,
 * pipeline stage, recent notes, and active engagements (v2). Provides a
 * jump-out link to the legacy client detail page until that is fully
 * retired in Phase 8.
 */
export default function UniversalClientDrawer({ clientId, open, onClose, workspaceId }) {
  const [client, setClient] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planDraft, setPlanDraft] = useState({ target_revenue: "", currency: "GBP", status: "active", goals_md: "", strategy_md: "", risk_md: "" });
  const [savingPlan, setSavingPlan] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const navigate = useNavigate();
  const { openClient } = useClientDrawer();

  const load = useCallback(async () => {
    if (!clientId || !open) return;
    setLoading(true);
    try {
      const [r, n] = await Promise.all([
        fetch(`${API}/clients/${clientId}`, { credentials: "include" }),
        fetch(`${API}/notes?parent_id=${clientId}&parent_type=client`, { credentials: "include" }).catch(() => null),
      ]);
      if (r.ok) setClient(await r.json());
      else throw new Error(`Client not found (${r.status})`);
      if (n && n.ok) {
        const nd = await n.json();
        setNotes(Array.isArray(nd) ? nd.slice(0, 5) : (nd?.notes || []).slice(0, 5));
      } else {
        setNotes([]);
      }

      // Engagements scoped by client_id (we filter client-side from the workspace list)
      if (workspaceId) {
        const er = await fetch(`${API}/v2/workspaces/${workspaceId}/engagements`, { credentials: "include" });
        if (er.ok) {
          const list = await er.json();
          setEngagements((list || []).filter((e) => e.client_id === clientId || (e.client_name && client?.company_name && e.client_name === client.company_name)));
        }

        // Phase 6: account plan + invoices
        const [pr, ir] = await Promise.all([
          fetch(`${API}/v2/workspaces/${workspaceId}/clients/${clientId}/account-plan`, { credentials: "include" }),
          fetch(`${API}/v2/workspaces/${workspaceId}/invoices?client_id=${clientId}`, { credentials: "include" }),
        ]);
        if (pr.ok) {
          const p = await pr.json();
          setPlan(p);
          if (p) {
            setPlanDraft({
              target_revenue: p.target_revenue ?? "",
              currency: p.currency || "GBP",
              status: p.status || "active",
              goals_md: p.goals_md || "",
              strategy_md: p.strategy_md || "",
              risk_md: p.risk_md || "",
            });
          }
        }
        if (ir.ok) setInvoices(await ir.json());
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, open, workspaceId, client?.company_name]);

  useEffect(() => { load(); }, [load]);

  const savePlan = async () => {
    setSavingPlan(true);
    try {
      const body = {
        ...planDraft,
        target_revenue: planDraft.target_revenue === "" ? null : parseFloat(planDraft.target_revenue),
      };
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/clients/${clientId}/account-plan`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Failed to save plan");
      const p = await r.json();
      setPlan(p);
      toast.success("Account plan saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingPlan(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="universal-client-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            {client?.company_name || "Client"}
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 py-6 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        )}

        {!loading && client && (
          <div className="space-y-5 mt-3">
            <section>
              <div className="flex flex-wrap gap-2 items-center">
                {client.stage && <Badge variant="outline" className="text-xs">{client.stage}</Badge>}
                {client.status && <Badge variant="outline" className="text-xs">{client.status}</Badge>}
                {client.tier && <Badge variant="outline" className="text-xs">{client.tier}</Badge>}
                {client.country && <Badge variant="outline" className="text-xs">{client.country}</Badge>}
                {client.sector && <Badge variant="outline" className="text-xs">{client.sector}</Badge>}
              </div>
              {client.industry && <p className="text-xs text-slate-500 mt-2">{client.industry}</p>}
              {client.notes && <p className="text-sm text-slate-700 mt-2">{client.notes}</p>}
            </section>

            {(client.related_entities?.length > 0) && (
              <section data-testid="ucd-related-entities">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Link2 className="h-4 w-4 text-indigo-600" /> Related entities</h3>
                <ul className="space-y-1">
                  {client.related_entities.map((r, i) => (
                    <li
                      key={r.client_id || i}
                      className="flex items-center justify-between border border-slate-200 rounded p-2 cursor-pointer hover:border-indigo-300"
                      onClick={() => r.client_id && openClient(r.client_id)}
                      data-testid={`ucd-related-${i}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{r.company_name || r.client_id}</p>
                        <p className="text-xs text-slate-500 truncate">{r.relationship}{r.recorded_by ? ` · ${r.recorded_by}` : ""}{r.recorded_at ? ` · ${r.recorded_at.slice(0, 10)}` : ""}</p>
                      </div>
                      <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {(client.aliases?.length > 0) && (
              <section data-testid="ucd-aliases">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Tag className="h-4 w-4 text-slate-500" /> Also known as</h3>
                <div className="flex flex-wrap gap-1.5">
                  {client.aliases.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-xs" data-testid={`ucd-alias-${i}`}>{a}</Badge>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Briefcase className="h-4 w-4 text-emerald-600" /> Active engagements</h3>
              {engagements.length === 0 ? (
                <p className="text-xs text-slate-400">No engagements linked to this client yet.</p>
              ) : (
                <ul className="space-y-1">
                  {engagements.map((e) => (
                    <li
                      key={e.engagement_id}
                      className="flex items-center justify-between border border-slate-200 rounded p-2 cursor-pointer hover:border-blue-300"
                      onClick={() => { onClose?.(); navigate(`/engagements?focus=${e.engagement_id}`); }}
                      data-testid={`ucd-engagement-${e.engagement_id}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-800">{e.name}</p>
                        <p className="text-xs text-slate-500">{e.status?.replace("_", " ")} · {e.currency} {e.contracted_value ?? "—"}</p>
                      </div>
                      <ExternalLink className="h-3 w-3 text-slate-400" />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><FileText className="h-4 w-4 text-purple-600" /> Recent notes</h3>
              {notes.length === 0 ? (
                <p className="text-xs text-slate-400">No notes yet.</p>
              ) : (
                <ul className="space-y-1 text-sm text-slate-700">
                  {notes.map((n, i) => (
                    <li key={n.note_id || n.id || i} className="border border-slate-200 rounded p-2">
                      <p className="text-xs text-slate-500">{n.created_at?.slice(0, 10) || n.timestamp?.slice(0, 10) || ""}</p>
                      <p className="text-sm whitespace-pre-line line-clamp-3">{n.content || n.text || n.body || n.note}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Mail className="h-4 w-4 text-amber-600" /> Key contacts</h3>
              {(client.key_contacts || client.contacts || []).length === 0 ? (
                <p className="text-xs text-slate-400">No contacts captured.</p>
              ) : (
                <ul className="space-y-1 text-sm text-slate-700">
                  {(client.key_contacts || client.contacts || []).slice(0, 6).map((c, i) => (
                    <li key={i} className="flex items-center justify-between border border-slate-100 rounded p-2 text-xs">
                      <span><strong>{c.name || c.full_name}</strong> {c.role && `· ${c.role}`}</span>
                      <span className="text-slate-500">{c.email || ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="ucd-account-plan-section">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Target className="h-4 w-4 text-indigo-600" /> Account plan ({new Date().getFullYear()})</h3>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[11px] text-slate-500">Target revenue</Label>
                  <Input data-testid="ucd-plan-target" type="number" value={planDraft.target_revenue} onChange={(e) => setPlanDraft({ ...planDraft, target_revenue: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Currency</Label>
                  <select value={planDraft.currency} onChange={(e) => setPlanDraft({ ...planDraft, currency: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
                    {["GBP", "USD", "NGN", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Status</Label>
                  <select value={planDraft.status} onChange={(e) => setPlanDraft({ ...planDraft, status: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
                    {["active", "paused", "churned"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-2">
                <Label className="text-[11px] text-slate-500">Key goals (markdown)</Label>
                <Textarea data-testid="ucd-plan-goals" rows={2} value={planDraft.goals_md} onChange={(e) => setPlanDraft({ ...planDraft, goals_md: e.target.value })} placeholder={"- Renew Q3 cohort\n- Expand to ops team"} />
              </div>
              <div className="mt-2">
                <Label className="text-[11px] text-slate-500">Strategy</Label>
                <Textarea rows={2} value={planDraft.strategy_md} onChange={(e) => setPlanDraft({ ...planDraft, strategy_md: e.target.value })} />
              </div>
              <div className="mt-2">
                <Label className="text-[11px] text-slate-500">Risks</Label>
                <Textarea rows={2} value={planDraft.risk_md} onChange={(e) => setPlanDraft({ ...planDraft, risk_md: e.target.value })} />
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={savePlan} disabled={savingPlan} data-testid="ucd-plan-save">
                  {savingPlan && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  <Save className="h-3 w-3 mr-1" /> Save plan
                </Button>
              </div>
            </section>

            <section data-testid="ucd-invoices-section">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Receipt className="h-4 w-4 text-emerald-600" /> Invoices</h3>
              {invoices.length === 0 ? (
                <p className="text-xs text-slate-400">No invoices linked to this client yet.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {invoices.map((inv) => (
                    <li key={inv.invoice_id} className="flex items-center justify-between border border-slate-200 rounded p-2" data-testid={`ucd-invoice-${inv.invoice_id}`}>
                      <div>
                        <p className="font-medium text-slate-800">{inv.number || inv.vendor || "Invoice"}</p>
                        <p className="text-xs text-slate-500">{inv.currency} {Number(inv.amount).toLocaleString()} · due {inv.due_date || "—"}</p>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">{inv.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="sticky bottom-0 bg-white pt-3 border-t border-slate-200 flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose} data-testid="ucd-close-btn">Close</Button>
              <Button
                size="sm"
                onClick={() => { onClose?.(); navigate(`/clients/${clientId}`); }}
                data-testid="ucd-open-full-btn"
                className="ml-auto"
              >
                Open full record
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
